/**
 * Utility functions for workflow history formatting and display
 */

/**
 * Format duration from milliseconds to human-readable string
 * @param {number} milliseconds - Duration in milliseconds
 * @returns {string} Formatted duration (e.g., "2h 15m 30s" or "450ms")
 */
export function formatDuration(milliseconds) {
  if (!milliseconds || milliseconds < 0) return '0ms';
  
  // If less than 1 second, show in milliseconds
  if (milliseconds < 1000) {
    return `${Math.round(milliseconds)}ms`;
  }
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

/**
 * Format duration in short format (for tables)
 * @param {number} milliseconds - Duration in milliseconds
 * @returns {string} Short formatted duration (e.g., "2m 15s" or "450ms")
 */
export function formatDurationShort(milliseconds) {
  if (!milliseconds || milliseconds < 0) return '0ms';
  
  // If less than 1 second, show in milliseconds
  if (milliseconds < 1000) {
    return `${Math.round(milliseconds)}ms`;
  }
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

/**
 * Format ISO timestamp to readable date
 * @param {string} isoString - ISO 8601 timestamp
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date
 */
export function formatTimestamp(isoString, options = {}) {
  if (!isoString) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...options
  };
  
  const date = new Date(isoString);
  return date.toLocaleString(undefined, defaultOptions);
}

/**
 * Format timestamp to relative time (e.g., "2 hours ago")
 * @param {string} isoString - ISO 8601 timestamp
 * @returns {string} Relative time string
 */
export function formatRelativeTime(isoString) {
  if (!isoString) return '';
  
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  
  return formatTimestamp(isoString);
}

/**
 * Get status color for badges/indicators
 * @param {string} status - Status: completed, failed, timeout, running, pending, paused, killed, skipped
 * @returns {string} CSS color value
 */
export function getStatusColor(status) {
  const colors = {
    completed: '#10b981', // green
    failed: '#ef4444',    // red
    errored: '#ef4444',   // red (alias for failed)
    timeout: '#f59e0b',   // orange
    running: '#3b82f6',   // blue
    pending: '#6b7280',   // gray
    paused: '#fbbf24',    // yellow
    killed: '#dc2626',    // dark red
    skipped: '#9ca3af'    // light gray
  };
  
  return colors[status] || '#6b7280'; // default gray
}

/**
 * Get status background color for badges
 * @param {string} status - Status: completed, failed, timeout, running, pending, paused, killed, skipped
 * @returns {string} CSS background color value
 */
export function getStatusBgColor(status) {
  const colors = {
    completed: '#2F353A', // dark bg
    failed: '#2F353A',    // dark bg
    errored: '#2F353A',   // dark bg
    timeout: '#2F353A',   // dark bg
    running: '#2F353A',   // dark bg
    pending: '#2F353A',   // dark bg
    paused: '#2F353A',    // dark bg
    killed: '#2F353A',    // dark bg
    skipped: '#2F353A'    // dark bg
  };
  
  return colors[status] || '#2F353A'; // default dark bg
}

/**
 * Get status icon/symbol
 * @param {string} status - Status: completed, failed, timeout, running, pending, paused, killed, skipped
 * @returns {string} Bootstrap icon class name
 */
export function getStatusIcon(status) {
  const icons = {
    completed: 'bi-check-circle-fill',
    failed: 'bi-x-circle-fill',
    errored: 'bi-x-circle-fill',
    timeout: 'bi-clock-fill',
    running: 'bi-arrow-clockwise',
    pending: 'bi-circle',
    paused: 'bi-pause-circle-fill',
    killed: 'bi-stop-circle-fill',
    skipped: 'bi-skip-forward-circle-fill'
  };
  
  return icons[status] || 'bi-question-circle';
}

/**
 * Get status label
 * @param {string} status - Status: completed, failed, timeout, running, pending, paused, killed, skipped
 * @returns {string} Human-readable status label
 */
export function getStatusLabel(status) {
  const labels = {
    completed: 'Completed',
    failed: 'Failed',
    errored: 'Failed',
    timeout: 'Timeout',
    running: 'Running',
    pending: 'Pending',
    paused: 'Paused',
    killed: 'Killed',
    skipped: 'Skipped'
  };
  
  return labels[status] || status;
}

/**
 * Calculate success rate from stats
 * @param {Object} stats - Stats object with total_executions and status_counts
 * @returns {number} Success rate as percentage (0-100)
 */
export function calculateSuccessRate(stats) {
  if (!stats || stats.total_executions === 0) return 0;
  const completed = stats.status_counts.completed || 0;
  return Math.round((completed / stats.total_executions) * 100 * 100) / 100;
}

/**
 * Calculate failure rate from stats
 * @param {Object} stats - Stats object with total_executions and status_counts
 * @returns {number} Failure rate as percentage (0-100)
 */
export function calculateFailureRate(stats) {
  if (!stats || stats.total_executions === 0) return 0;
  const failed = (stats.status_counts.failed || 0) + (stats.status_counts.errored || 0) + (stats.status_counts.timeout || 0);
  return Math.round((failed / stats.total_executions) * 100 * 100) / 100;
}

/**
 * Truncate UUID or long string with ellipsis
 * @param {string} str - String to truncate
 * @param {number} length - Max length before truncation
 * @returns {string} Truncated string
 */
export function truncateUuid(str, length = 8) {
  if (!str) return '';
  if (str.length <= length) return str;
  return `${str.substring(0, length)}...`;
}

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} True if successful
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

/**
 * Format log level with color
 * @param {string} level - Log level: info, warn, error, debug
 * @returns {Object} Object with color and label
 */
export function getLogLevelStyle(level) {
  const styles = {
    info: { color: '#3b82f6', label: 'INFO' },
    warn: { color: '#f59e0b', label: 'WARN' },
    error: { color: '#ef4444', label: 'ERROR' },
    debug: { color: '#6b7280', label: 'DEBUG' }
  };
  
  return styles[level] || styles.info;
}
